import type { BondOrder } from '../domain/editor.ts';
import type {
  MarkushDrawingDocument,
  MarkushFragment,
  MarkushScaffold,
  RGroupDefinition,
  VariablePosition
} from '../domain/markushDocument.ts';
import { isSingleRing } from '../domain/markushDocument.ts';
import { elementSymbol } from './elements.ts';
import { loadRdKit } from './rdkitAdapter.ts';

// #663 (Markush M6): opens Markush structures from other tools -- CXSMILES text, MDL molfiles
// (V2000/V3000) and RGfiles -- as a chem-drawing.v11 Markush document. Owner decisions: both
// formats in M6, and **import what fits, list what was skipped** rather than refusing a file with
// a feature our model lacks (R-group logic, nested R-groups, fused-ring attachment, stereo, ...).
//
// CXSMILES goes through RDKit (which parses it and writes a V3000 molblock with coordinates); the
// `$...$` atom labels (`_R1`) are read from the CXSMILES text itself, because RDKit's molblock keeps
// the R atom but drops its number. Molfiles/RGfiles are read directly.

export interface MarkushImportResult {
  readonly document: MarkushDrawingDocument;
  /** Human-readable notes on what was not imported (empty when everything fit). */
  readonly skipped: readonly string[];
}

interface CtabAtom {
  readonly symbol: string;
  readonly x: number;
  readonly y: number;
  charge: number;
  isotope?: number;
  rgroups: number[];
  atomList?: { readonly elements: readonly number[]; readonly not: boolean };
  attachmentPoint?: number;
  /** CXSMILES `$` label, e.g. `_R1`. */
  label?: string;
}

interface CtabBond {
  readonly a: number;
  readonly b: number;
  readonly type: number;
  readonly stereo: boolean;
  endpoints?: readonly number[];
}

interface Sgroup {
  readonly type: string;
  readonly atoms: readonly number[];
  readonly label?: string;
}

interface Ctab {
  readonly atoms: CtabAtom[];
  readonly bonds: CtabBond[];
  readonly sgroups: Sgroup[];
}

interface ParsedMolfile {
  readonly root: Ctab;
  readonly members: Map<number, Ctab[]>;
  /** R-group number -> a note when that group carries occurrence/RestH/if-then logic. */
  readonly logic: Map<number, string>;
}

const SYMBOL_TO_Z: ReadonlyMap<string, number> = new Map(
  Array.from({ length: 118 }, (_, index) => [elementSymbol(index + 1), index + 1] as const)
);
const ANGSTROM = 30; // drawing units per Ångström: RDKit's ~1.5 Å bond -> 45 px, the editor default
const PADDING = 40;

export async function importMarkush(text: string): Promise<MarkushImportResult> {
  const input = text.replace(/\r\n?/g, '\n').trim();
  if (!input) throw new Error('Paste CXSMILES or open a molfile / RGfile to import.');
  const skipped: string[] = [];
  let parsed: ParsedMolfile;
  if (/M {2}V30 /.test(input)) parsed = parseV3000(input);
  else if (/V2000|\$RGP|\$MDL|\$CTAB/.test(input)) parsed = parseV2000File(input);
  else parsed = await parseCxsmiles(input);
  return { document: toMarkush(parsed, skipped), skipped };
}

// --- CXSMILES ---------------------------------------------------------------------------------

async function parseCxsmiles(input: string): Promise<ParsedMolfile> {
  const rdkit = await loadRdKit();
  const molecule = rdkit.get_mol(input);
  try {
    if (!molecule?.is_valid()) throw new Error('This is not valid CXSMILES, a molfile or an RGfile.');
    molecule.set_new_coords();
    const parsed = parseV3000(molecule.get_v3Kmolblock());
    const labels = /\$([^$]*)\$/.exec(input.slice(input.indexOf('|')))?.[1]?.split(';') ?? [];
    labels.forEach((label, index) => {
      const atom = parsed.root.atoms[index];
      if (atom && label) atom.label = label;
    });
    return parsed;
  } finally {
    molecule?.delete();
  }
}

// --- V3000 ------------------------------------------------------------------------------------

/** Splits a V3000 line into tokens, keeping `KEY=(a b c)` and quoted strings whole. */
function tokens(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let depth = 0;
  let quoted = false;
  for (const char of line) {
    if (char === '"') quoted = !quoted;
    if (!quoted && char === '(') depth++;
    if (!quoted && char === ')') depth--;
    if (char === ' ' && depth === 0 && !quoted) {
      if (current) result.push(current);
      current = '';
    } else current += char;
  }
  if (current) result.push(current);
  return result;
}

function keyValues(parts: readonly string[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const part of parts) {
    const at = part.indexOf('=');
    if (at > 0) map.set(part.slice(0, at).toUpperCase(), part.slice(at + 1));
  }
  return map;
}

/** `(3 1 2 3)` -> [1, 2, 3] (the leading count dropped). */
function numberList(value: string | undefined): number[] {
  if (!value) return [];
  const numbers = value.replace(/[()]/g, '').trim().split(/\s+/).map(Number);
  return numbers.slice(1);
}

function parseV3000(text: string): ParsedMolfile {
  // Logical lines: strip "M  V30 ", join "-" continuations.
  const lines: string[] = [];
  for (const raw of text.split('\n')) {
    if (!raw.startsWith('M  V30 ')) continue;
    const body = raw.slice(7);
    const previous = lines.at(-1);
    if (previous !== undefined && previous.endsWith('-')) lines[lines.length - 1] = previous.slice(0, -1) + body;
    else lines.push(body);
  }
  let root: Ctab | undefined;
  const members = new Map<number, Ctab[]>();
  const logic = new Map<number, string>();
  let rgroup: number | undefined;
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]!.trim();
    const rgroupStart = /^BEGIN RGROUP (\d+)/.exec(line);
    if (rgroupStart) rgroup = Number(rgroupStart[1]);
    else if (line.startsWith('END RGROUP')) rgroup = undefined;
    else if (line.startsWith('RLOGIC') && rgroup !== undefined) {
      const [, thenR = '0', restH = '0', occurrence = '""'] = tokens(line);
      if (thenR !== '0' || restH !== '0' || occurrence.replace(/"/g, '').trim() !== '') {
        logic.set(rgroup, `R-group logic on R${rgroup} (occurrence / RestH / if-then) was ignored`);
      }
    } else if (line === 'BEGIN CTAB') {
      const { ctab, next } = parseV3000Ctab(lines, index + 1);
      index = next;
      if (rgroup === undefined) root ??= ctab;
      else members.set(rgroup, [...(members.get(rgroup) ?? []), ctab]);
    }
  }
  if (!root) throw new Error('No structure (CTAB) found in this molfile.');
  return { root, members, logic };
}

function parseV3000Ctab(lines: readonly string[], start: number): { ctab: Ctab; next: number } {
  const ctab: Ctab = { atoms: [], bonds: [], sgroups: [] };
  const atomIndex = new Map<number, number>();
  let section = '';
  let index = start;
  for (; index < lines.length; index++) {
    const line = lines[index]!.trim();
    if (line === 'END CTAB') break;
    const begin = /^BEGIN (ATOM|BOND|SGROUP)/.exec(line);
    if (begin) {
      section = begin[1]!;
      continue;
    }
    if (line.startsWith('END ')) {
      section = '';
      continue;
    }
    const parts = tokens(line);
    if (section === 'ATOM') {
      const [id, type = 'C', x = '0', y = '0'] = parts;
      const extra = keyValues(parts.slice(6));
      const atom: CtabAtom = {
        symbol: type,
        x: Number(x),
        y: Number(y),
        charge: Number(extra.get('CHG') ?? 0),
        rgroups: numberList(extra.get('RGROUPS'))
      };
      if (extra.has('MASS')) atom.isotope = Number(extra.get('MASS'));
      if (extra.has('ATTCHPT')) atom.attachmentPoint = Number(extra.get('ATTCHPT'));
      const list = /^(NOT\s*)?\[([^\]]+)]$/.exec(type);
      if (list)
        atom.atomList = {
          not: !!list[1],
          elements: list[2]!.split(',').map((symbol) => SYMBOL_TO_Z.get(symbol.trim()) ?? 0)
        };
      atomIndex.set(Number(id), ctab.atoms.length);
      ctab.atoms.push(atom);
    } else if (section === 'BOND') {
      const [, type = '1', a = '0', b = '0'] = parts;
      const extra = keyValues(parts.slice(4));
      const bond: CtabBond = {
        a: atomIndex.get(Number(a)) ?? -1,
        b: atomIndex.get(Number(b)) ?? -1,
        type: Number(type),
        stereo: extra.has('CFG') && extra.get('CFG') !== '0'
      };
      if (extra.get('ATTACH') === 'ANY' && extra.has('ENDPTS')) {
        bond.endpoints = numberList(extra.get('ENDPTS')).map((id) => atomIndex.get(id) ?? -1);
      }
      ctab.bonds.push(bond);
    } else if (section === 'SGROUP') {
      const [, type = ''] = parts;
      const extra = keyValues(parts.slice(3));
      ctab.sgroups.push({
        type,
        atoms: numberList(extra.get('ATOMS')).map((id) => atomIndex.get(id) ?? -1),
        ...(extra.has('LABEL') ? { label: extra.get('LABEL')!.replace(/"/g, '') } : {})
      });
    }
  }
  return { ctab, next: index };
}

// --- V2000 molfile / RGfile -------------------------------------------------------------------

function parseV2000File(text: string): ParsedMolfile {
  const lines = text.split('\n');
  if (!/\$RGP|\$CTAB|\$MDL/.test(text)) {
    // A plain molfile: 3 header lines, then the counts line -- found by content, since the header's
    // (often blank) first line may have been trimmed away with the surrounding whitespace.
    const counts = lines.findIndex((line) => /V2000\s*$/.test(line));
    if (counts < 0) throw new Error('Expected a V2000 counts line in this molfile.');
    return { root: parseV2000Ctab(lines, counts), members: new Map(), logic: new Map() };
  }
  // RGfile: the root and every member are "$CTAB ... $END CTAB" blocks; members follow "$RGP n".
  let root: Ctab | undefined;
  const members = new Map<number, Ctab[]>();
  const logic = new Map<number, string>();
  let rgroup: number | undefined;
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]!.trim();
    if (line === '$RGP') {
      rgroup = Number(lines[index + 1]!.trim());
      index++;
    } else if (line === '$END RGP') rgroup = undefined;
    else if (line === '$CTAB') {
      const ctab = parseV2000Ctab(lines, index + 1);
      if (rgroup === undefined) {
        root ??= ctab;
        // M LOG lines live in the root CTAB's property block.
        for (let scan = index + 1; scan < lines.length && lines[scan]!.trim() !== '$END CTAB'; scan++) {
          const log = /^M {2}LOG\s+\d+\s+(\d+)\s+(\d+)\s+(\d+)\s*(.*)$/.exec(lines[scan]!);
          if (log && (log[2] !== '0' || log[3] !== '0' || log[4]!.trim() !== '')) {
            logic.set(Number(log[1]), `R-group logic on R${log[1]} (occurrence / RestH / if-then) was ignored`);
          }
        }
      } else members.set(rgroup, [...(members.get(rgroup) ?? []), ctab]);
    }
  }
  if (!root) throw new Error('No structure (CTAB) found in this RGfile.');
  return { root, members, logic };
}

/** A V2000 CTAB whose counts line is lines[start]. */
function parseV2000Ctab(lines: readonly string[], start: number): Ctab {
  const counts = lines[start] ?? '';
  if (!/V2000/.test(counts)) throw new Error('Expected a V2000 counts line in this molfile.');
  const atomCount = Number(counts.slice(0, 3));
  const bondCount = Number(counts.slice(3, 6));
  const ctab: Ctab = { atoms: [], bonds: [], sgroups: [] };
  for (let index = 0; index < atomCount; index++) {
    const line = lines[start + 1 + index] ?? '';
    const chargeCode = Number(line.slice(36, 39) || 0);
    ctab.atoms.push({
      symbol: line.slice(31, 34).trim(),
      x: Number(line.slice(0, 10)),
      y: Number(line.slice(10, 20)),
      charge: chargeCode ? 4 - chargeCode : 0,
      rgroups: []
    });
  }
  for (let index = 0; index < bondCount; index++) {
    const line = lines[start + 1 + atomCount + index] ?? '';
    ctab.bonds.push({
      a: Number(line.slice(0, 3)) - 1,
      b: Number(line.slice(3, 6)) - 1,
      type: Number(line.slice(6, 9)),
      stereo: Number(line.slice(9, 12) || 0) !== 0
    });
  }
  const sgroupTypes = new Map<number, string>();
  const sgroupAtoms = new Map<number, number[]>();
  const sgroupLabels = new Map<number, string>();
  for (let index = start + 1 + atomCount + bondCount; index < lines.length; index++) {
    const line = lines[index] ?? '';
    if (line.startsWith('M  END')) break;
    const fields = line.slice(6).trim().split(/\s+/).map(Number);
    const pairs = (from: number) => {
      const out: [number, number][] = [];
      for (let at = from; at + 1 < fields.length; at += 2) out.push([fields[at]!, fields[at + 1]!]);
      return out;
    };
    if (line.startsWith('M  CHG')) for (const [atom, value] of pairs(1)) ctab.atoms[atom - 1]!.charge = value;
    else if (line.startsWith('M  ISO')) for (const [atom, value] of pairs(1)) ctab.atoms[atom - 1]!.isotope = value;
    else if (line.startsWith('M  RGP')) for (const [atom, value] of pairs(1)) ctab.atoms[atom - 1]!.rgroups.push(value);
    else if (line.startsWith('M  APO'))
      for (const [atom, value] of pairs(1)) ctab.atoms[atom - 1]!.attachmentPoint = value;
    else if (line.startsWith('M  ALS')) {
      const atom = Number(line.slice(7, 10));
      const not = line.slice(14, 15) === 'T';
      const elements = line
        .slice(16)
        .trim()
        .split(/\s+/)
        .map((symbol) => SYMBOL_TO_Z.get(symbol) ?? 0);
      ctab.atoms[atom - 1]!.atomList = { not, elements };
    } else if (line.startsWith('M  STY')) {
      // Fixed-width entries: 4-char Sgroup number, space, 3-char type code.
      const count = Number(line.slice(6, 9));
      for (let entry = 0; entry < count; entry++) {
        const chunk = line.slice(9 + entry * 8, 17 + entry * 8);
        sgroupTypes.set(Number(chunk.slice(0, 4)), chunk.slice(5, 8).trim());
      }
    } else if (line.startsWith('M  SAL')) {
      const sgroup = Number(line.slice(7, 10));
      const atoms = line
        .slice(14)
        .trim()
        .split(/\s+/)
        .map((value) => Number(value) - 1);
      sgroupAtoms.set(sgroup, [...(sgroupAtoms.get(sgroup) ?? []), ...atoms]);
    } else if (line.startsWith('M  SMT')) {
      sgroupLabels.set(Number(line.slice(7, 10)), line.slice(11).trim());
    }
  }
  for (const [sgroup, type] of sgroupTypes) {
    const label = sgroupLabels.get(sgroup);
    ctab.sgroups.push({ type, atoms: sgroupAtoms.get(sgroup) ?? [], ...(label !== undefined ? { label } : {}) });
  }
  return ctab;
}

// --- mapping to the Markush model ---------------------------------------------------------------

interface Layout {
  readonly width: number;
  readonly height: number;
  point(atom: CtabAtom): { x: number; y: number };
}

/** Molfile coordinates (Å, y up) to drawing units (y down), padded into a positive box. */
function layout(atoms: readonly CtabAtom[]): Layout {
  const xs = atoms.map((atom) => atom.x);
  const ys = atoms.map((atom) => atom.y);
  const minX = Math.min(...xs, 0);
  const maxY = Math.max(...ys, 0);
  const width = Math.max(1, (Math.max(...xs, 0) - minX) * ANGSTROM + 2 * PADDING);
  const height = Math.max(1, (maxY - Math.min(...ys, 0)) * ANGSTROM + 2 * PADDING);
  return {
    width,
    height,
    point: (atom) => ({
      x: Math.round(((atom.x - minX) * ANGSTROM + PADDING) * 100) / 100,
      y: Math.round(((maxY - atom.y) * ANGSTROM + PADDING) * 100) / 100
    })
  };
}

function isDummy(atom: CtabAtom): boolean {
  return ['R#', 'R', 'A', 'Q', '*', 'X', 'L'].includes(atom.symbol) && !atom.atomList;
}

function rLabel(atom: CtabAtom): string | undefined {
  if (atom.rgroups[0] !== undefined) return `R${atom.rgroups[0]}`;
  const cx = /^_R(\d+)$/.exec(atom.label ?? '');
  return cx ? `R${cx[1]}` : undefined;
}

function toMarkush(parsed: ParsedMolfile, skipped: string[]): MarkushDrawingDocument {
  const { root } = parsed;
  const note = (message: string) => {
    if (!skipped.includes(message)) skipped.push(message);
  };
  if (
    root.bonds.some((bond) => bond.type === 4) ||
    [...parsed.members.values()].flat().some((ctab) => ctab.bonds.some((bond) => bond.type === 4))
  ) {
    throw new Error('This file uses aromatic bond type 4; export it with Kekulé (alternating single/double) bonds.');
  }
  if ([root, ...[...parsed.members.values()].flat()].some((ctab) => ctab.bonds.some((bond) => bond.stereo))) {
    note('Stereo bonds were imported as plain bonds (Markush scaffolds carry no stereochemistry yet)');
  }
  parsed.logic.forEach((message) => note(message));

  const removed = new Set<number>();
  const rGroups: Record<string, RGroupDefinition> = {};
  const positions: VariablePosition[] = [];
  const usedLabels = new Set<string>();
  const ids = root.atoms.map((_, index) => `a${index + 1}`);
  const extraAtoms: { id: string; atom: CtabAtom }[] = [];
  const nextLabel = () => {
    let n = 1;
    const taken = new Set([
      ...usedLabels,
      ...root.atoms.flatMap((atom) => rLabel(atom) ?? []),
      ...[...parsed.members.keys()].map((key) => `R${key}`)
    ]);
    while (taken.has(`R${n}`)) n++;
    usedLabels.add(`R${n}`);
    return `R${n}`;
  };

  // Variable (ring-interior) attachment bonds: star atom -- substituent, ENDPTS = ring atoms.
  const attachmentPlaceholders = new Set<number>();
  for (const bond of root.bonds) {
    if (!bond.endpoints) continue;
    const star = isDummy(root.atoms[bond.a]!) && !rLabel(root.atoms[bond.a]!) ? bond.a : bond.b;
    const substituent = star === bond.a ? bond.b : bond.a;
    const ringIds = bond.endpoints.map((index) => ids[index]!);
    removed.add(star);
    const scaffoldGraph = {
      nodes: ids.map((id) => ({ id })),
      edges: root.bonds
        .filter((other) => other !== bond)
        .map((other, index) => ({ id: `e${index}`, source: ids[other.a]!, target: ids[other.b]! }))
    };
    if (!isSingleRing(scaffoldGraph, ringIds)) {
      note('An attachment point over more than one ring (fused system) was skipped — single rings only for now');
      for (const index of component(root, substituent, star)) removed.add(index);
      continue;
    }
    const existingLabel = rLabel(root.atoms[substituent]!);
    if (existingLabel) {
      // The substituent is itself an R atom: it becomes the (bond-less) attachment placeholder.
      attachmentPlaceholders.add(substituent);
      usedLabels.add(existingLabel);
      positions.push({
        kind: 'attachment-point',
        atomId: ids[substituent]!,
        ringAtomIds: ringIds,
        rGroupLabel: existingLabel
      });
    } else {
      // A concrete substituent: it becomes the one member of a new R-group, attached anywhere on the ring.
      const atoms = component(root, substituent, star);
      atoms.forEach((index) => removed.add(index));
      const label = nextLabel();
      rGroups[label] = { kind: 'enumerated', members: [fragmentFrom(root, atoms, substituent)] };
      const id = `ap${positions.length + 1}`;
      extraAtoms.push({ id, atom: root.atoms[star]! });
      positions.push({ kind: 'attachment-point', atomId: id, ringAtomIds: ringIds, rGroupLabel: label });
    }
  }

  // R atoms and atom lists.
  root.atoms.forEach((atom, index) => {
    if (removed.has(index) || attachmentPlaceholders.has(index)) return;
    // An R atom without a number (a plain molfile's "R", a CXSMILES "*" with no _R label next to
    // numbered ones) still marks an R-group position: it takes the next free label.
    const label = rLabel(atom) ?? (atom.symbol === 'R' || atom.symbol === 'R#' ? nextLabel() : undefined);
    if (atom.rgroups.length > 1)
      note(`An atom allowing several R-groups (R${atom.rgroups.join('/R')}) was imported as R${atom.rgroups[0]}`);
    if (label) {
      usedLabels.add(label);
      positions.push({ kind: 'r-group', atomId: ids[index]!, rGroupLabel: label });
    } else if (atom.atomList) {
      const elements = atom.atomList.elements.filter((z) => z > 0);
      if (atom.atomList.not || elements.length < 2) {
        note('A "NOT" atom list (or one with fewer than two elements) was imported as carbon');
      } else
        positions.push({
          kind: 'atom-list',
          atomId: ids[index]!,
          allowedAtomicNumbers: [...new Set(elements)].sort((a, b) => a - b)
        });
    } else if (isDummy(atom)) {
      note(`A generic query atom (${atom.symbol}) was imported as carbon`);
    }
  });

  // Repeat units (SRU brackets); other bracket kinds are skipped.
  for (const sgroup of root.sgroups) {
    if (sgroup.type === 'SUP' || sgroup.type === 'DAT') continue; // abbreviations / data: nothing to import
    if (sgroup.type !== 'SRU') {
      note(`A ${sgroup.type} bracket was skipped (only repeat units, SRU, are supported)`);
      continue;
    }
    const atoms = sgroup.atoms.filter((index) => index >= 0 && !removed.has(index));
    const inside = new Set(atoms);
    const bondIds = root.bonds.flatMap((bond, index) =>
      inside.has(bond.a) && inside.has(bond.b) ? [`b${index + 1}`] : []
    );
    const range = /^(\d+)\s*-\s*(\d+)$/.exec(sgroup.label ?? '');
    const single = /^(\d+)$/.exec(sgroup.label ?? '');
    const countMin = range ? Number(range[1]) : single ? Number(single[1]) : 1;
    const countMax = range ? Number(range[2]) : single ? Number(single[1]) : undefined;
    positions.push({
      kind: 'repeat-unit',
      fragmentAtomIds: atoms.map((index) => ids[index]!),
      fragmentBondIds: bondIds,
      countMin,
      ...(countMax !== undefined ? { countMax } : {})
    });
  }

  // R-group members (RGfile / V3000 RGROUP blocks).
  for (const [number, members] of parsed.members) {
    const fragments: MarkushFragment[] = [];
    for (const member of members) {
      if (member.atoms.some((atom) => rLabel(atom))) {
        note(`A member of R${number} containing its own R-group (nested) was skipped`);
        continue;
      }
      let attach = member.atoms.findIndex((atom) => atom.attachmentPoint === 1 || atom.attachmentPoint === 3);
      if (attach < 0) {
        attach = 0;
        note(`A member of R${number} had no attachment point; its first atom was used`);
      }
      fragments.push(
        fragmentFrom(
          member,
          member.atoms.map((_, index) => index),
          attach
        )
      );
    }
    if (fragments.length > 0) rGroups[`R${number}`] = { kind: 'enumerated', members: fragments };
  }

  // The scaffold: every atom not removed; R atoms and atom-list atoms become atom-less placeholders.
  const placeholderIds = new Set(positions.flatMap((position) => ('atomId' in position ? [position.atomId] : [])));
  const kept = root.atoms.flatMap((atom, index) => (removed.has(index) ? [] : [{ id: ids[index]!, atom }]));
  const all = [...kept, ...extraAtoms];
  const box = layout(all.map(({ atom }) => atom));
  const scaffold: MarkushScaffold = {
    graph: {
      nodes: all.map(({ id }) => ({ id })),
      edges: root.bonds.flatMap((bond, index) =>
        removed.has(bond.a) || removed.has(bond.b)
          ? []
          : [{ id: `b${index + 1}`, source: ids[bond.a]!, target: ids[bond.b]! }]
      )
    },
    atoms: Object.fromEntries(
      kept.flatMap(({ id, atom }) => {
        if (placeholderIds.has(id)) return [];
        const z = SYMBOL_TO_Z.get(atom.symbol) ?? 6;
        return [[id, { atomicNumber: z, ...(atom.isotope ? { isotope: atom.isotope } : {}) }]];
      })
    ),
    bonds: Object.fromEntries(
      root.bonds.flatMap((bond, index) =>
        removed.has(bond.a) || removed.has(bond.b)
          ? []
          : [[`b${index + 1}`, { order: Math.min(3, Math.max(1, bond.type)) as BondOrder, type: 'covalent' as const }]]
      )
    ),
    formalCharges: Object.fromEntries(
      kept.flatMap(({ id, atom }) => (placeholderIds.has(id) ? [] : [[id, atom.charge]]))
    ),
    coordinateSet: {
      width: box.width,
      height: box.height,
      coordinates: Object.fromEntries(all.map(({ id, atom }) => [id, box.point(atom)]))
    }
  };
  return { schemaVersion: 'chem-drawing.v11', kind: 'markush', scaffold, rGroups, variablePositions: positions };
}

/** Atoms reachable from `start` without passing through `blocked`. */
function component(ctab: Ctab, start: number, blocked: number): number[] {
  const seen = new Set([start]);
  const queue = [start];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const bond of ctab.bonds) {
      const other = bond.a === current ? bond.b : bond.b === current ? bond.a : -1;
      if (other >= 0 && other !== blocked && !seen.has(other)) {
        seen.add(other);
        queue.push(other);
      }
    }
  }
  return [...seen];
}

function fragmentFrom(ctab: Ctab, atomIndexes: readonly number[], attach: number): MarkushFragment {
  const inside = new Set(atomIndexes);
  const id = (index: number) => `f${index + 1}`;
  const box = layout(atomIndexes.map((index) => ctab.atoms[index]!));
  const bonds = ctab.bonds.flatMap((bond, index) =>
    inside.has(bond.a) && inside.has(bond.b) ? [{ bond, index }] : []
  );
  return {
    graph: {
      nodes: atomIndexes.map((index) => ({ id: id(index) })),
      edges: bonds.map(({ bond, index }) => ({ id: `fb${index + 1}`, source: id(bond.a), target: id(bond.b) }))
    },
    atoms: Object.fromEntries(
      atomIndexes.map((index) => [id(index), { atomicNumber: SYMBOL_TO_Z.get(ctab.atoms[index]!.symbol) ?? 6 }])
    ),
    bonds: Object.fromEntries(
      bonds.map(({ bond, index }) => [
        `fb${index + 1}`,
        { order: Math.min(3, Math.max(1, bond.type)) as BondOrder, type: 'covalent' as const }
      ])
    ),
    formalCharges: Object.fromEntries(atomIndexes.map((index) => [id(index), ctab.atoms[index]!.charge])),
    coordinateSet: {
      width: box.width,
      height: box.height,
      coordinates: Object.fromEntries(atomIndexes.map((index) => [id(index), box.point(ctab.atoms[index]!)]))
    },
    openValenceAtomId: id(attach)
  };
}
